import { motion, type Variants } from 'framer-motion';
import { cn } from '@/utils/tailwind.util';
import { type AiBuilderPageSurfaceMode } from '@/views/build/features/ai-builder-chat/components/AiBuilderShellTransition';
import { BuildAiButtons } from './BuildAiButtons';
import { BuildQuizButtons } from './BuildQuizButtons';
import { BuildQuizFloatingButtons } from './BuildQuizFloatingButtons';
import { QuizList } from './QuizList';
import { QuizSliderSidebar } from './QuizSlider';
type BuildStepQuizProps = {
  /**
   * 현재 step이 'quiz'인지 여부.
   * false일 때는 display:none으로 화면에서만 숨기고 mount는 유지한다.
   * 이로써 카드 N개의 옵저버/구독/RHF 바인딩이 step 토글에도 살아남는다.
   */
  active: boolean;
  surfaceMode?: AiBuilderPageSurfaceMode;
};
export const BuildStepQuiz = ({
  active,
  surfaceMode = 'page'
}: BuildStepQuizProps) => {
  return (
    // display:none → IntersectionObserver / ResizeObserver / layout이 자동 비활성화.
    // 접근성/탭 포커스도 display:none이 자동으로 제거하므로 inert는 불필요.
    <div aria-hidden={!active} style={{
      display: active ? 'block' : 'none'
    }} data-sentry-component="BuildStepQuiz" data-sentry-source-file="BuildStepQuiz.tsx">
      <BuildStepQuizDefaultContent active={active} surfaceMode={surfaceMode} data-sentry-element="BuildStepQuizDefaultContent" data-sentry-source-file="BuildStepQuiz.tsx" />
    </div>
  );
};
const BuildStepQuizDefaultContent = ({
  active,
  surfaceMode
}: {
  active: boolean;
  surfaceMode: AiBuilderPageSurfaceMode;
}) => {
  const isPageRestoring = surfaceMode === 'page-restoring';
  const usesAiShellLayout = surfaceMode !== 'page';
  const shouldShowAiEntry = surfaceMode === 'page' || isPageRestoring;
  return <>
      {surfaceMode === 'page' && <QuizSliderSidebar transitionState="visible" />}

      <motion.div key={'quiz'}
    // 항상 mount 상태이므로 AnimatePresence enter/exit 대신 active 변화에 따라
    // 직접 opacity + x를 애니메이트한다.
    // 기존 동작 재현: active=true 진입 시 x:-20 → 0으로 슬라이드 인.
    // initial={false}로 첫 mount에서 깜빡임을 막는다.
    initial={false} animate={{
      opacity: active ? 1 : 0,
      x: active ? 0 : -20
    }} transition={{
      duration: 0.2,
      ease: 'easeInOut'
    }} style={{
      pointerEvents: 'auto',
      position: 'relative'
    }} data-sentry-element="unknown" data-sentry-source-file="BuildStepQuiz.tsx">
        <div className={cn('mx-auto flex w-full flex-col', usesAiShellLayout ? 'max-w-[650px]' : 'max-w-[1034px] items-center gap-lg px-md mobile-lg:px-lg tablet-lg:flex-row tablet-lg:items-start tablet-lg:justify-center tablet-lg:px-0')}>
          <div className="flex w-full max-w-[650px] flex-none flex-col">
            <motion.div data-ai-builder-entry-wrapper aria-hidden={!shouldShowAiEntry} initial={false} animate={shouldShowAiEntry ? 'visible' : 'hidden'} variants={AI_ENTRY_WRAPPER_VARIANTS} style={{
            pointerEvents: shouldShowAiEntry ? 'auto' : 'none'
          }} data-sentry-element="unknown" data-sentry-source-file="BuildStepQuiz.tsx">
              <BuildAiButtons layoutMode={usesAiShellLayout ? 'ai-shell' : 'page'} data-sentry-element="BuildAiButtons" data-sentry-source-file="BuildStepQuiz.tsx" />
            </motion.div>
            <QuizList emptyDescriptionTransitionState={shouldShowAiEntry ? 'visible' : 'hidden'} data-sentry-element="QuizList" data-sentry-source-file="BuildStepQuiz.tsx" />
            <BuildQuizButtons layoutMode={usesAiShellLayout ? 'ai-shell' : 'page'} data-sentry-element="BuildQuizButtons" data-sentry-source-file="BuildStepQuiz.tsx" />
            {surfaceMode === 'page' && <BuildQuizFloatingButtons />}
          </div>
        </div>
      </motion.div>
    </>;
};
const AI_ENTRY_WRAPPER_VARIANTS: Variants = {
  hidden: {
    height: 0,
    opacity: 0,
    overflow: 'hidden',
    y: -8,
    transition: {
      duration: 0.22,
      ease: 'easeInOut'
    },
    transitionEnd: {
      visibility: 'hidden'
    }
  },
  visible: {
    height: 'auto',
    opacity: 1,
    overflow: 'hidden',
    visibility: 'visible',
    y: 0,
    transition: {
      duration: 0.24,
      ease: 'easeOut'
    },
    transitionEnd: {
      overflow: 'visible'
    }
  }
};